import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  TextField,
  Alert,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { supabase } from '../services/supabase';
import { format } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const SCHEDULE_LABELS = {
  morning: '오전 9시',
  noon: '오후 12시',
  evening: '오후 6시',
};

const SCHEDULE_COLORS = {
  morning: '#2196f3',
  noon: '#ff9800',
  evening: '#9c27b0',
};

function ShellPieceGrants() {
  const [grants, setGrants] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterDate, setFilterDate] = useState(format(new Date(), 'yyyy-MM-dd'));

  useEffect(() => {
    fetchGrants();
  }, []);

  const fetchGrants = async () => {
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('shell_piece_grants')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      const grantData = data || [];
      setGrants(grantData);

      // Lookup users
      const userIds = [...new Set(grantData.map((g) => g.user_id))];
      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, email')
          .in('id', userIds);

        if (usersError) throw usersError;

        const map = {};
        (usersData || []).forEach((u) => {
          map[u.id] = u;
        });
        setUsersMap(map);
      }
    } catch (err) {
      console.error('Failed to fetch shell piece grants:', err);
      setError('등껍질 지급 내역을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // Today's stats by schedule_type
  const todayStats = useMemo(() => {
    const stats = { morning: 0, noon: 0, evening: 0 };
    grants.forEach((g) => {
      if (g.grant_date === filterDate && stats[g.schedule_type] !== undefined) {
        stats[g.schedule_type] += 1;
      }
    });
    return stats;
  }, [grants, filterDate]);

  // Filtered grants for DataGrid
  const filteredGrants = useMemo(() => {
    if (!filterDate) return grants;
    return grants.filter((g) => g.grant_date === filterDate);
  }, [grants, filterDate]);

  // Chart data: daily grants grouped by date and schedule_type
  const chartData = useMemo(() => {
    const dateMap = {};
    grants.forEach((g) => {
      if (!dateMap[g.grant_date]) {
        dateMap[g.grant_date] = { date: g.grant_date, morning: 0, noon: 0, evening: 0 };
      }
      if (dateMap[g.grant_date][g.schedule_type] !== undefined) {
        dateMap[g.grant_date][g.schedule_type] += 1;
      }
    });
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date)).slice(-14);
  }, [grants]);

  const columns = [
    {
      field: 'user_name',
      headerName: '이름',
      width: 130,
      valueGetter: (value, row) => usersMap[row.user_id]?.name || '-',
    },
    {
      field: 'user_email',
      headerName: '이메일',
      width: 220,
      valueGetter: (value, row) => usersMap[row.user_id]?.email || '-',
    },
    {
      field: 'grant_date',
      headerName: '지급 날짜',
      width: 130,
    },
    {
      field: 'schedule_type',
      headerName: '시간대',
      width: 120,
      valueFormatter: (value) => SCHEDULE_LABELS[value] || value,
    },
    {
      field: 'pieces_count',
      headerName: '지급 수량',
      width: 110,
      valueFormatter: (value) => value?.toLocaleString() || '0',
    },
    {
      field: 'created_at',
      headerName: '생성일시',
      width: 170,
      valueFormatter: (value) =>
        value ? format(new Date(value), 'yyyy-MM-dd HH:mm:ss') : '',
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        등껍질 지급 현황
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Date Filter */}
      <Box sx={{ mb: 3 }}>
        <TextField
          label="조회 날짜"
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          size="small"
        />
      </Box>

      {/* Today Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {['morning', 'noon', 'evening'].map((type) => (
          <Grid key={type} size={{ xs: 12, sm: 4 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom variant="overline">
                      {SCHEDULE_LABELS[type]}
                    </Typography>
                    <Typography variant="h4">{todayStats[type]}명</Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: SCHEDULE_COLORS[type] + '20',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography sx={{ color: SCHEDULE_COLORS[type], fontWeight: 'bold', fontSize: 18 }}>
                      {type === 'morning' ? '오전' : type === 'noon' ? '오후' : '저녁'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Bar Chart */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            일별 지급 현황 (최근 14일)
          </Typography>
          <Box sx={{ width: '100%', height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(label) => `날짜: ${label}`}
                  formatter={(value, name) => [
                    `${value}명`,
                    SCHEDULE_LABELS[name] || name,
                  ]}
                />
                <Legend formatter={(value) => SCHEDULE_LABELS[value] || value} />
                <Bar dataKey="morning" fill={SCHEDULE_COLORS.morning} />
                <Bar dataKey="noon" fill={SCHEDULE_COLORS.noon} />
                <Bar dataKey="evening" fill={SCHEDULE_COLORS.evening} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            지급 내역 ({filterDate})
          </Typography>
          <Box sx={{ height: 500, width: '100%' }}>
            {filteredGrants.length > 0 ? (
              <DataGrid
                rows={filteredGrants}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                columnVisibilityModel={{ id: false }}
              />
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">해당 날짜에 지급 내역이 없습니다.</Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ShellPieceGrants;
